import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, ChevronRight } from 'lucide-react';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandSeparator, CommandShortcut,
} from './ui/command';
import { GUIDES, GROUP_ORDER } from '../data/helpGuides';
import { helpAPI } from '../lib/api';

// Global ⌘K / Ctrl+K docs command palette — jump straight to any guide or step.
export default function DocsCommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    const openEvt = () => setOpen(true);
    window.addEventListener('lockene:open-docs-palette', openEvt);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('lockene:open-docs-palette', openEvt);
    };
  }, []);

  const go = (guideId, step = null) => {
    setOpen(false);
    helpAPI.track(guideId).catch(() => {});
    const q = step != null ? `?guide=${guideId}&step=${step}` : `?guide=${guideId}`;
    navigate(`/help${q}`);
  };

  const groups = GROUP_ORDER
    .map((group) => ({ group, items: GUIDES.filter((g) => g.group === group) }))
    .filter((g) => g.items.length);

  return (
    <CommandDialog open={open} onOpenChange={setOpen} title="Search documentation">
      <CommandInput placeholder="Search docs — jump to a guide or a step…" data-testid="docs-palette-input" />
      <CommandList data-testid="docs-palette-list">
        <CommandEmpty>No docs found.</CommandEmpty>
        {groups.map(({ group, items }, gi) => (
          <React.Fragment key={group}>
            {gi > 0 && <CommandSeparator />}
            <CommandGroup heading={group}>
              {items.map((g) => {
                const Icon = g.icon;
                return (
                  <React.Fragment key={g.id}>
                    <CommandItem
                      value={`${g.title} ${(g.tags || []).join(' ')}`}
                      onSelect={() => go(g.id)}
                      data-testid={`docs-palette-guide-${g.id}`}
                    >
                      <Icon className="text-primary" />
                      <span className="font-medium">{g.title}</span>
                      {g.plan && <CommandShortcut className="text-amber-500">{g.plan}+</CommandShortcut>}
                    </CommandItem>
                    {g.steps.map((s, i) => (
                      <CommandItem
                        key={`${g.id}-${i}`}
                        value={`${g.title} ${s.title}`}
                        onSelect={() => go(g.id, i)}
                        className="pl-8 text-muted-foreground"
                        data-testid={`docs-palette-step-${g.id}-${i}`}
                      >
                        <ChevronRight className="opacity-50" />
                        <span>{s.title}</span>
                        <CommandShortcut className="normal-case tracking-normal">{g.title}</CommandShortcut>
                      </CommandItem>
                    ))}
                  </React.Fragment>
                );
              })}
            </CommandGroup>
          </React.Fragment>
        ))}
        <CommandSeparator />
        <CommandGroup heading="More">
          <CommandItem value="open help center documentation" onSelect={() => { setOpen(false); navigate('/help'); }} data-testid="docs-palette-open-center">
            <BookOpen /> <span>Open the full Help Center</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
